import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, X } from 'lucide-react';
import { toast } from 'sonner';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// PDF.js worker'ı ayarla - CDN'den doğru versiyonu kullan
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API = '/api';

export default function PDFViewerPage() {
  const { playId } = useParams();
  const navigate = useNavigate();
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    fetchPDF();
    
    // Cleanup blob URL on unmount
    return () => {
      if (pdfUrl && typeof pdfUrl === 'string') {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [playId]);

  const fetchPDF = async () => {
    try {
      // Backend streams PDF from S3 (no CORS issues)
      const response = await axios.get(`${API}/plays/${playId}/pdf`, {
        responseType: 'blob'
      });

      // Create blob URL for react-pdf
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(blob);
      setPdfUrl(fileURL);
    } catch (error) {
      console.error('Error loading PDF:', error);
      const errorMsg = error.response?.data?.detail || 'PDF yüklenirken bir hata oluştu';
      toast.error(errorMsg);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages));
  };

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.2, 3.0));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.2, 0.5));
  };

  const handleClose = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 bg-gray-800 border-b border-gray-700 z-50">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4">
            <Button
              onClick={handleClose}
              variant="ghost"
              className="text-gray-300 hover:text-white"
              data-testid="close-pdf-btn"
            >
              <X className="w-5 h-5 mr-2" />
              Kapat
            </Button>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-4">
            {/* Page Navigation */}
            <div className="flex items-center gap-2">
              <Button
                onClick={goToPrevPage}
                disabled={pageNumber <= 1}
                variant="ghost"
                size="sm"
                className="text-gray-300 hover:text-white disabled:opacity-50"
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <span className="text-gray-300 text-sm min-w-[100px] text-center">
                {numPages ? `${pageNumber} / ${numPages}` : 'Yükleniyor...'}
              </span>
              <Button
                onClick={goToNextPage}
                disabled={pageNumber >= numPages}
                variant="ghost"
                size="sm"
                className="text-gray-300 hover:text-white disabled:opacity-50"
                data-testid="next-page-btn"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center gap-2 border-l border-gray-700 pl-4">
              <Button
                onClick={zoomOut}
                disabled={scale <= 0.5}
                variant="ghost"
                size="sm"
                className="text-gray-300 hover:text-white disabled:opacity-50"
                data-testid="zoom-out-btn"
              >
                <ZoomOut className="w-5 h-5" />
              </Button>
              <span className="text-gray-300 text-sm min-w-[60px] text-center">
                {Math.round(scale * 100)}%
              </span>
              <Button
                onClick={zoomIn}
                disabled={scale >= 3.0}
                variant="ghost"
                size="sm"
                className="text-gray-300 hover:text-white disabled:opacity-50"
                data-testid="zoom-in-btn"
              >
                <ZoomIn className="w-5 h-5" />
              </Button>
            </div>
          </div>

          <div className="w-20"></div>
        </div>
      </div>

      {/* PDF Content */}
      <div className="pt-16 flex justify-center items-start min-h-screen p-4">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-300 mt-4">PDF yükleniyor...</p>
          </div>
        ) : pdfUrl ? (
          <div className="bg-gray-800 shadow-2xl" data-testid="pdf-document">
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={(error) => {
                console.error('Error loading PDF:', error);
                toast.error('PDF yüklenemedi');
              }}
              loading={
                <div className="text-center py-12 text-gray-300">
                  PDF hazırlanıyor...
                </div>
              }
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={true}
                renderAnnotationLayer={true}
              />
            </Document>
          </div>
        ) : null}
      </div>
    </div>
  );
}
